/**
 * Signed-out dialog (imsight#106): shown when the session ends while the app
 * is open. Static backdrop, no close button, Esc disabled; the app stays
 * visible but inert behind it. One action per kind.
 */

import Modal from 'bootstrap/js/src/modal.js';
import type { SessionEndKind } from '../core/session-ended';

export interface SignedOutDialogOptions {
  t: (key: string, params?: Record<string, string>) => string;
  kind: SessionEndKind;
  organizationName: string | null;
  onSignIn: () => void;
  onSignOut: () => void;
}

export default class SignedOutDialog {
  constructor(private readonly options: SignedOutDialogOptions) {}

  /** Build the modal element. All text goes through textContent (no HTML). */
  render(): HTMLElement {
    const { t, kind, organizationName, onSignIn, onSignOut } = this.options;
    // expired and signed_out (#110) both offer Sign in again; access_ended Sign out.
    const signIn = kind !== 'access_ended';
    const titleKey = {
      expired: 'signedOut.expiredTitle',
      signed_out: 'signedOut.otherTabTitle',
      access_ended: 'signedOut.accessEndedTitle',
    }[kind];

    const modal = document.createElement('div');
    modal.className = 'modal fade';
    modal.id = 'signed-out-dialog';
    modal.tabIndex = -1;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'signed-out-dialog-title');

    const dialog = document.createElement('div');
    dialog.className = 'modal-dialog modal-dialog-centered';
    const content = document.createElement('div');
    content.className = 'modal-content';

    const header = document.createElement('div');
    header.className = 'modal-header';
    const title = document.createElement('h2');
    title.className = 'modal-title fs-5';
    title.id = 'signed-out-dialog-title';
    title.textContent = t(titleKey);
    header.appendChild(title);

    const body = document.createElement('div');
    body.className = 'modal-body';
    const message = document.createElement('p');
    message.className = 'mb-0';
    message.textContent =
      kind === 'access_ended'
        ? t('signedOut.accessEndedMessage', {
            organization: organizationName || t('signedOut.yourOrganization'),
          })
        : t(
            kind === 'signed_out'
              ? 'signedOut.otherTabMessage'
              : 'signedOut.expiredMessage'
          );
    body.appendChild(message);

    const footer = document.createElement('div');
    footer.className = 'modal-footer';
    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'btn btn-primary';
    action.dataset.signedOutAction = signIn ? 'sign-in' : 'sign-out';
    action.textContent = t(signIn ? 'signedOut.signIn' : 'signedOut.signOut');
    action.addEventListener('click', () => (signIn ? onSignIn() : onSignOut()));
    footer.appendChild(action);

    content.append(header, body, footer);
    dialog.appendChild(content);
    modal.appendChild(dialog);
    return modal;
  }

  show(): void {
    const element = this.render();
    document.body.appendChild(element);
    Modal.getOrCreateInstance(element, {
      backdrop: 'static',
      keyboard: false,
      focus: true,
    }).show();
  }
}
